import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Users, Activity, AlertCircle, Mail, Trash2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import { api, getUser } from "../lib/api";
import { Page, Spinner } from "../components/Brand";
import { URGENCY_LABEL, STATUS_LABEL } from "../lib/fr";

const DEMO_EMAILS = ["im@contracepsur.health","obgyn@contracepsur.health","admin@contracepsur.health"];

function KPITile({ label, value, hint, testid }) {
  return (
    <div className="cs-card p-5 flex flex-col gap-1" data-testid={testid}>
      <div className="cs-label">{label}</div>
      <div className="font-semibold mt-2" style={{ fontSize: 36, letterSpacing: "-0.8px" }}>{value}</div>
      {hint && <div className="text-[12px] mt-1" style={{ color: "var(--cs-text-muted)" }}>{hint}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const user = getUser();
  const queryClient = useQueryClient();
  const [testEmail, setTestEmail] = useState(user?.email || "");
  const [testBusy, setTestBusy] = useState(false);
  const [demoStats, setDemoStats] = useState(null);
  const [demoBusy, setDemoBusy] = useState(false);
  const isDemo = !!user && DEMO_EMAILS.includes(user.email);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-metrics"],
    queryFn: async () => (await api.get("/admin/metrics")).data,
    refetchInterval: 30000,
  });
  const m = data || { total: 0, by_status: {}, by_urgency: {}, median_scheduling_delay_minutes: 0, completion_rate: 0 };

  useEffect(() => {
    api.get("/admin/demo-stats").then((r) => setDemoStats(r.data)).catch(() => setDemoStats(null));
  }, []);

  async function sendTestEmail() {
    if (!testEmail) { toast.error("Saisissez une adresse email"); return; }
    setTestBusy(true);
    try {
      const { data } = await api.post("/admin/test-email", { to: testEmail });
      toast.success(`Email envoyé depuis ${data.from} (id ${data.id?.slice(0, 8)}…)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec d'envoi");
    } finally { setTestBusy(false); }
  }

  async function cleanupDemo() {
    if (!window.confirm("Supprimer définitivement les comptes et données de démonstration ? Cette action est irréversible.")) return;
    setDemoBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup-demo");
      toast.success(`Nettoyage terminé : ${data.deleted_users} compte(s), ${data.deleted_referrals} référence(s), ${data.deleted_events} événement(s)`);
      setDemoStats({ demo_users: [], demo_referral_count: 0 });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["admin-metrics"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du nettoyage");
    } finally { setDemoBusy(false); }
  }

  async function wipeAllReferrals() {
    const total = m.total || 0;
    const typed = window.prompt(
      `⚠️ Supprimer DÉFINITIVEMENT toutes les ${total} référence(s), l'agenda OB-GYN et l'historique des notifications ?\n\nLes comptes utilisateurs sont conservés. La numérotation repart à COB-AA-0001.\n\nTapez SUPPRIMER (en majuscules) pour confirmer :`
    );
    if (typed !== "SUPPRIMER") {
      if (typed !== null) toast.error("Suppression annulée — texte de confirmation incorrect");
      return;
    }
    setDemoBusy(true);
    try {
      const { data } = await api.post("/admin/wipe-all-referrals", { confirm: "SUPPRIMER" });
      toast.success(`Suppression terminée : ${data.deleted_referrals} référence(s) et ${data.deleted_events} événement(s) supprimés`);
      queryClient.invalidateQueries({ queryKey: ["admin-metrics"] });
      queryClient.invalidateQueries({ queryKey: ["obgyn-inbox"] });
      queryClient.invalidateQueries({ queryKey: ["obgyn-agenda"] });
      queryClient.invalidateQueries({ queryKey: ["my-referrals"] });
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["unread-count"] });
      // refresh demo stats too (since referrals from demo IM are now gone)
      api.get("/admin/demo-stats").then((r) => setDemoStats(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la suppression");
    } finally { setDemoBusy(false); }
  }

  function fmtDelay(min) {
    if (!min) return "—";
    if (min < 60) return `${Math.round(min)} min`;
    const h = min / 60;
    if (h < 48) return `${h.toFixed(1)} h`;
    return `${(h/24).toFixed(1)} j`;
  }

  return (
    <Page>
      <Toaster position="top-center" richColors />
      <div className="flex items-end justify-between mb-6 md:mb-8">
        <div>
          <div className="cs-label">Administration · QI</div>
          <h1 className="font-semibold mt-1" style={{ fontSize: 32, letterSpacing: "-0.6px" }}>Tableau de bord</h1>
          <div className="text-[14px] mt-1" style={{ color: "var(--cs-text-muted)" }}>{user?.name}</div>
        </div>
        <button onClick={() => navigate("/admin/users")} className="cs-btn-primary" data-testid="manage-users-button">
          <Users size={18} /> Gérer les utilisateurs
        </button>
      </div>

      {isLoading ? (
        <div className="cs-card p-10 flex justify-center"><Spinner /></div>
      ) : (
        <div className="flex flex-col gap-6" data-testid="admin-kpi">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPITile label="Total références" value={m.total} testid="kpi-total" />
            <KPITile label="Taux de complétion" value={`${m.completion_rate}%`} testid="kpi-completion" />
            <KPITile label="Délai médian de programmation" value={fmtDelay(m.median_scheduling_delay_minutes)} testid="kpi-delay" />
            <KPITile label="En attente" value={m.by_status.pending || 0} testid="kpi-pending" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="cs-card p-5">
              <div className="cs-label mb-3 flex items-center gap-1.5"><Activity size={12} /> Répartition par statut</div>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(STATUS_LABEL).map(([k, l]) => (
                  <div key={k} className="flex items-center justify-between p-3 rounded-[12px]"
                       style={{ background: "var(--cs-bg)" }} data-testid={`status-stat-${k}`}>
                    <span className="text-[13px]" style={{ color: "var(--cs-text-muted)" }}>{l}</span>
                    <span className="font-semibold text-[16px]">{m.by_status[k] || 0}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="cs-card p-5">
              <div className="cs-label mb-3 flex items-center gap-1.5"><AlertCircle size={12} /> Répartition par urgence</div>
              <div className="grid grid-cols-1 gap-3">
                {Object.entries(URGENCY_LABEL).map(([k, l]) => {
                  const v = m.by_urgency[k] || 0;
                  const pct = m.total ? Math.round((v / m.total) * 100) : 0;
                  const colorMap = { routine: "#9CA3AF", urgent: "#F59E0B", very_urgent: "#DC2626" };
                  return (
                    <div key={k} data-testid={`urgency-stat-${k}`}>
                      <div className="flex justify-between text-[13px] mb-1.5">
                        <span style={{ color: "var(--cs-text-muted)" }}>{l}</span>
                        <span className="font-semibold">{v} <span style={{ color: "var(--cs-text-subtle)", fontWeight: 400 }}>({pct}%)</span></span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--cs-border)" }}>
                        <div style={{ width: `${pct}%`, height: "100%", background: colorMap[k] }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="cs-card p-5" data-testid="test-email-card">
            <div className="cs-label mb-2 flex items-center gap-1.5"><Mail size={12} /> Test de configuration email</div>
            <p className="text-[13px] mb-3" style={{ color: "var(--cs-text-muted)" }}>
              Envoie un email de test via Resend depuis l&apos;expéditeur configuré pour vérifier la délivrance.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                className="cs-input flex-1"
                placeholder="destinataire@exemple.fr"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                data-testid="test-email-input"
              />
              <button onClick={sendTestEmail} disabled={testBusy} className="cs-btn-primary" data-testid="test-email-send">
                {testBusy ? <Spinner /> : <><Mail size={16} /> Envoyer le test</>}
              </button>
            </div>
          </div>

          {demoStats && (demoStats.demo_users?.length > 0 || demoStats.demo_referral_count > 0) && (
            <div className="cs-card p-5" data-testid="cleanup-demo-card" style={{ borderColor: "rgba(220,38,38,0.25)" }}>
              <div className="cs-label mb-2 flex items-center gap-1.5" style={{ color: "var(--cs-danger)" }}>
                <Trash2 size={12} /> Données de démonstration détectées
              </div>
              <p className="text-[13px] mb-3" style={{ color: "var(--cs-text-muted)" }}>
                <b>{demoStats.demo_users.length}</b> compte(s) de démonstration et <b>{demoStats.demo_referral_count}</b> référence(s) de test sont encore en base.
                Supprimez-les avant le passage en production réelle.
              </p>
              <ul className="text-[12px] mb-3" style={{ color: "var(--cs-text-subtle)" }}>
                {demoStats.demo_users.map((u) => (
                  <li key={u.email}>• {u.email} ({u.role})</li>
                ))}
              </ul>
              {isDemo ? (
                <div className="p-3 rounded-[12px] text-[12px]" style={{ background: "rgba(245,158,11,0.10)", color: "var(--cs-accent)" }}>
                  Vous êtes connecté avec un compte de démonstration. <b>Invitez d&apos;abord un administrateur réel</b>, connectez-vous avec lui, puis revenez sur cette page pour effectuer le nettoyage.
                </div>
              ) : (
                <button onClick={cleanupDemo} disabled={demoBusy} className="cs-btn-danger-outline" data-testid="cleanup-demo-button">
                  {demoBusy ? <Spinner /> : <><Trash2 size={16} /> Supprimer toutes les données de démo</>}
                </button>
              )}
            </div>
          )}

          {m.total > 0 && (
            <div className="cs-card p-5" data-testid="wipe-all-card" style={{ borderColor: "rgba(220,38,38,0.25)" }}>
              <div className="cs-label mb-2 flex items-center gap-1.5" style={{ color: "var(--cs-danger)" }}>
                <Trash2 size={12} /> Vider toutes les références et l&apos;agenda
              </div>
              <p className="text-[13px] mb-3" style={{ color: "var(--cs-text-muted)" }}>
                Supprime définitivement les <b>{m.total}</b> référence(s), l&apos;agenda OB-GYN et toutes les notifications.
                Les comptes utilisateurs sont conservés. La numérotation repart à <b>COB-AA-0001</b>.
              </p>
              <button onClick={wipeAllReferrals} disabled={demoBusy} className="cs-btn-danger-outline" data-testid="wipe-all-button">
                {demoBusy ? <Spinner /> : <><Trash2 size={16} /> Tout vider (références + agenda)</>}
              </button>
            </div>
          )}
        </div>
      )}
    </Page>
  );
}
